'use client'

import { useState } from 'react'
import Image from 'next/image'
import { useParams } from 'next/navigation'

import { useMedusaSearchParametersStore } from '@lib/store/medusa-search/medusa-search-parameters.store'
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  startOfMonth,
} from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useTranslations } from 'next-intl'

import Expand from '../../public/expand.svg'

const localeMap: Record<string, Locale> = {
  fr,
  en: enUS,
}
const getMonthDays = (y: number, m: number) =>
  eachDayOfInterval({
    start: startOfMonth(new Date(y, m)),
    end: endOfMonth(new Date(y, m)),
  })

export const DateRangePickerView = () => {
  const { searchDateRange, setSearchDateRange } =
    useMedusaSearchParametersStore()
  const today = new Date()
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const { countryCode } = useParams() as { countryCode: string }

  const t = useTranslations('SearchPage')
  const daysOfWeek = [
    t('shortDay.mon'),
    t('shortDay.tue'),
    t('shortDay.wed'),
    t('shortDay.thu'),
    t('shortDay.fri'),
    t('shortDay.sat'),
    t('shortDay.sun'),
  ]

  const { startDate, endDate } = searchDateRange

  const days = getMonthDays(currentYear, currentMonth)

  const handleDayClick = (day: Date) => {
    const clickedDay = day.toISOString()

    let s = startDate
    let e = endDate

    if (!s || (s && e)) {
      s = clickedDay
      e = null
    } else if (isBefore(day, parseISO(s))) {
      s = clickedDay
    } else {
      e = clickedDay
    }

    setSearchDateRange(s, e)
  }

  const isInRange = (d: Date) =>
    startDate &&
    endDate &&
    isAfter(d, parseISO(startDate)) &&
    isBefore(d, parseISO(endDate))

  const isSel = (d: Date) =>
    (startDate && isSameDay(d, parseISO(startDate))) ||
    (endDate && isSameDay(d, parseISO(endDate)))

  return (
    <div className="flex flex-col px-10 pb-3 pt-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => {
            if (currentMonth === 0) {
              setCurrentMonth(11)
              setCurrentYear(currentYear - 1)
            } else {
              setCurrentMonth(currentMonth - 1)
            }
          }}
        >
          <Image src={Expand} alt="prev" className="h-6 w-6 rotate-90" />
        </button>
        <div className="text-[19px] font-medium">
          {format(new Date(currentYear, currentMonth), 'MMMM yyyy', {
            locale: localeMap[countryCode],
          })}
        </div>
        <button
          onClick={() => {
            if (currentMonth === 11) {
              setCurrentMonth(0)
              setCurrentYear(currentYear + 1)
            } else {
              setCurrentMonth(currentMonth + 1)
            }
          }}
        >
          <Image src={Expand} alt="next" className="h-6 w-6 -rotate-90" />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-gray-600">
        {daysOfWeek.map((d, i) => (
          <div key={i} className="font-medium">
            {d}
          </div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1">
        {days.map((day) => (
          <div
            key={day.toISOString()}
            onClick={() => handleDayClick(day)}
            className={`cursor-pointer rounded-full py-2 text-center text-sm ${
              isSel(day)
                ? 'bg-green text-white'
                : isInRange(day)
                  ? 'bg-gray-200 text-black'
                  : 'text-gray-800 hover:bg-gray-100'
            }`}
          >
            {format(day, 'd')}
          </div>
        ))}
      </div>

      <div className="mb-4 mt-6 flex w-full items-center justify-around px-10 text-[17px]">
        <div>
          <strong>{t('start')} :</strong>
          {startDate ? format(parseISO(startDate), 'dd/MM/yyyy') : ' - -'}
        </div>
        <div>
          <strong>{t('end')} :</strong>
          {endDate ? format(parseISO(endDate), 'dd/MM/yyyy') : ' - -'}
        </div>
      </div>
    </div>
  )
}
